/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState, useEffect } from "react";
import { Image, TouchableOpacity, View, ScrollView } from "react-native";
import parse from "html-react-parser";
import SwipeableViews from "react-swipeable-views";
import { Animated } from "react-animated-css";

import { Text, styles } from "UpduoLibrary";
import {
  colorWithOpacity,
  skaleBlack,
  skaleBorderLightGray,
  skaleGray,
  skaleLightGray,
  skaleTextSecondaryGray,
  skaleWhite,
  upduoPrimaryBlue,
} from "config/UpduoColors";
import { processSkillString } from "config/Utilities";
import PartnerA from "assets/partnerA.png";
import PartnerB from "assets/partnerB.png";

function StepCardPreview({ step, isTeacher, stepIndex, sessionLength }) {
  const [isDetailShown, setIsDetailShown] = useState(false);
  const [isDetailPresent, setIsDetailPresent] = useState(false);
  const stepContent = step[isTeacher ? "teacher" : "learner"];

  useEffect(() => {
    if (isDetailShown === false) {
      setTimeout(() => {
        setIsDetailPresent(false);
      }, 600);
    }
  }, [isDetailShown]);

  const _displayButtonText = () => {
    if (stepIndex < sessionLength - 1) {
      return "NEXT CARD";
    } else if (stepIndex === sessionLength - 1) {
      return "END CONVERSATION";
    }
    return "NEXT QUESTION";
  };

  const detailHtml = stepContent.detail;
  const detailButton = (
    <TouchableOpacity
      onPress={() => {
        setIsDetailShown(true);
        setIsDetailPresent(true);
      }}
    >
      <Text isHeading={true} style={{ color: upduoPrimaryBlue, fontSize: 16 }}>
        {"MORE"}
      </Text>
    </TouchableOpacity>
  );

  const hideDetailButton = (
    <TouchableOpacity
      style={[styles.floatingButton, { backgroundColor: skaleWhite }]}
      onPress={() => setIsDetailShown(false)}
    >
      <Text isHeading={true} style={{ color: upduoPrimaryBlue, fontSize: 16 }}>
        {"HIDE"}
      </Text>
    </TouchableOpacity>
  );

  const detailSlider = (
    <View style={{ position: "absolute", bottom: 0, left: 0, right: 0 }}>
      <Animated
        animationIn={"slideInUp"}
        animationOut={"slideOutDown"}
        isVisible={isDetailShown}
        animationInDuration={500}
        animationOutDuration={500}
      >
        <View
          style={{
            position: "absolute",
            top: -20,
            right: 15,
            zIndex: 5,
          }}
        >
          {hideDetailButton}
        </View>
        <View
          style={{
            backgroundColor: colorWithOpacity(skaleWhite, 0.95),
            borderTopLeftRadius: 18,
            borderTopRightRadius: 18,
            paddingTop: 20,
            paddingBottom: 30,
          }}
        >
          <ScrollView
            style={{
              height: 250,
              width: 350,
              paddingHorizontal: 25,
            }}
          >
            <Text>{parse(detailHtml)}</Text>
          </ScrollView>
        </View>
      </Animated>
    </View>
  );

  return (
    <View
      style={{
        height: 750,
        justifyContent: "flex-end",
        overflow: "hidden",
      }}
    >
      <View
        style={{
          backgroundColor: skaleWhite,
          height: 300,
          overflow: "hidden",
          borderRadius: 18,
          borderWidth: 1,
          borderColor: skaleBorderLightGray,
        }}
      >
        <View
          style={{
            alignItems: "center",
            overflow: "hidden",
            paddingTop: 24,
            paddingBottom: 10,
          }}
        >
          <Text
            style={{
              color: skaleBlack,
              fontSize: 20,
              fontWeight: "bold",
            }}
          >
            {`${step.title}/${sessionLength}`}
          </Text>
        </View>

        <View
          style={{
            flex: 1,
            marginHorizontal: 20,
          }}
        >
          <ScrollView contentContainerStyle={{ flex: 1 }}>
            <View style={{ bottom: 0 }}>
              <Text
                style={{
                  fontSize: 16,
                  color: skaleTextSecondaryGray,
                }}
              >
                {stepContent.heading}
              </Text>
            </View>
          </ScrollView>
          <View style={{ alignItems: "flex-end", marginTop: 10 }}>
            {detailHtml ? detailButton : null}
          </View>
        </View>
        <View style={{ marginHorizontal: 20, marginTop: 10, marginBottom: 20 }}>
          <Text
            style={{
              fontSize: 14,
              color: skaleGray,
            }}
          >
            {`about ${step.minutes} min${step.minutes > 1 ? "s" : ""}`}
          </Text>
          <View
            style={{
              height: 60,
              borderRadius: 30,
              marginTop: 10,
              backgroundColor: upduoPrimaryBlue,
              alignSelf: "stretch",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text
              style={{ fontSize: 16, color: skaleWhite, fontWeight: "500" }}
            >
              {_displayButtonText()}
            </Text>
          </View>
        </View>
      </View>
      {isDetailPresent ? detailSlider : null}
    </View>
  );
}

interface PhonePreviewProps {
  skillSessionSteps: any[];
  teacherRole: string;
  learnerRole: string;
  skillName: string;
  isTeacher: boolean;
  title: string;
  cardIndex: number;
  communityName: string;
  onChangeIndex?: (index: number) => void;
}
export default function PhonePreviewClassic({
  skillSessionSteps,
  teacherRole,
  learnerRole,
  skillName,
  isTeacher,
  title,
  cardIndex,
  communityName,
  onChangeIndex = () => {},
}: PhonePreviewProps) {
  const newSkillSessionSteps = JSON.parse(JSON.stringify(skillSessionSteps));
  // Perform string replacements for all relevant step card fields
  const roles = ["teacher", "learner"];
  const stringTypes = ["heading", "detail"];
  for (let i = 0; i < newSkillSessionSteps.length; i++) {
    for (const role of roles) {
      for (const stringType of stringTypes) {
        newSkillSessionSteps[i][role][stringType] = processSkillString(
          newSkillSessionSteps[i][role][stringType],
          "Alice",
          "Bob",
          teacherRole,
          learnerRole,
          skillName,
          isTeacher,
          communityName
        );
      }
    }
  }

  const sessionStepCards = newSkillSessionSteps.map((step, stepIndex) => {
    return (
      <StepCardPreview
        step={step}
        key={stepIndex}
        stepIndex={stepIndex}
        isTeacher={isTeacher}
        sessionLength={newSkillSessionSteps.length}
      />
    );
  });

  const phoneWidth = 350;
  const phoneHeight = 750;
  return (
    <View style={{ alignItems: "center", paddingTop: 40 }}>
      <View style={[styles.skillRoleHeading]}>
        <View
          style={{
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: skaleLightGray,
            overflow: "hidden",
          }}
        >
          <Image
            source={isTeacher ? PartnerB : PartnerA}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: 40,
              height: 60,
            }}
          />
        </View>
        <Text
          style={{
            color: skaleBlack,
            fontSize: 16,
            fontWeight: "500",
            margin: 12,
          }}
        >
          {title}
        </Text>
      </View>
      <View
        style={{
          height: phoneHeight,
          width: phoneWidth,
          overflow: "hidden",
          borderWidth: 1,
          borderColor: skaleBorderLightGray,
          borderRadius: 18,
        }}
      >
        <Image
          source={isTeacher ? PartnerA : PartnerB}
          style={{
            width: phoneWidth,
            height: phoneHeight,
            resizeMode: "cover",
          }}
        />
        <Image
          style={{
            position: "absolute",
            overflow: "hidden",
            top: 30,
            right: 30,
            width: 70,
            height: 110,
            borderWidth: 1,
            borderBottomWidth: 0,
            borderColor: skaleBorderLightGray,
            borderRadius: 8,
            backgroundColor: skaleWhite,
            resizeMode: "cover",
          }}
          source={isTeacher ? PartnerB : PartnerA}
        />
        <View
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: phoneWidth,
            height: phoneHeight,
          }}
        >
          <SwipeableViews
            disabled={!isTeacher}
            index={cardIndex}
            onChangeIndex={(newIndex) => {
              if (isTeacher && onChangeIndex) {
                onChangeIndex(newIndex);
              }
            }}
          >
            {sessionStepCards}
          </SwipeableViews>
        </View>
      </View>
    </View>
  );
}
